import {
  cloudPathFromRel,
  cloudPathName,
  parseCloudPath,
  tryParseCloudPath,
} from '@/shared/utils/cloudPath'

/** What a link into the user's storage points at, for its icon and style. */
export type CloudLinkKind = 'folder' | 'result' | 'report' | 'image' | 'table' | 'file'

/** Font Awesome icon per kind; `result` matches the result chip in the composer (FileTagChip). */
export const CLOUD_LINK_ICONS: Record<CloudLinkKind, string> = {
  folder: 'fa-folder',
  result: 'fa-chart-simple',
  report: 'fa-file-lines',
  image: 'fa-file-image',
  table: 'fa-table',
  file: 'fa-file',
}

const EXTENSION_KINDS: Record<string, CloudLinkKind> = {
  box: 'result',
  html: 'report',
  htm: 'report',
  pdf: 'report',
  md: 'report',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  svg: 'image',
  webp: 'image',
  tif: 'image',
  tiff: 'image',
  csv: 'table',
  tsv: 'table',
  xls: 'table',
  xlsx: 'table',
}

/** Kind of a `cloud:` path: a folder by its trailing `/`, a file by its extension. */
export function cloudLinkKind(cloudPath: string): CloudLinkKind {
  if (parseCloudPath(cloudPath).isFolder) return 'folder'
  const name = cloudPathName(cloudPath)
  const dot = name.lastIndexOf('.')
  const extension = dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
  return EXTENSION_KINDS[extension] ?? 'file'
}

/**
 * The `cloud:` path of a same-origin OmicsBox deep link (the MCP's `open_url` / `open_dir_url`):
 * `/results?file=<rel>` (optionally `&name=`) is a file, `/files?path=<rel>` a folder. Null for
 * anything else, or a link with other parameters, which stays a plain web link.
 */
export function cloudPathFromAppHref(href: string | undefined, origin: string): string | null {
  if (!href) return null
  let url: URL
  try {
    url = new URL(href, origin)
  } catch {
    return null
  }
  if (url.origin !== origin) return null
  const keys = Array.from(url.searchParams.keys())
  const only = (...allowed: string[]) => keys.every((key) => allowed.includes(key))
  const file = url.searchParams.get('file')
  const folder = url.searchParams.get('path')
  if (url.pathname === '/results' && file && only('file', 'name')) return toCloudPath(file)
  if (url.pathname === '/files' && folder !== null && only('path')) return toCloudPath(folder, true)
  return null
}

/** The `cloud:` path of a relative path from a deep link, or null if it is not a valid one. */
function toCloudPath(rel: string, isFolder = false): string | null {
  try {
    const cloud = cloudPathFromRel(rel, {isFolder})
    return tryParseCloudPath(cloud) ? cloud : null
  } catch {
    return null
  }
}
